import {
  ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY,
  ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY,
} from "@/store/modules/viewScope"
import { clearObservedScopeToken } from "@/utils/tokenScopeSession"
import { invalidateBusinessContextEpoch } from "./epoch"
import { advanceBusinessContextRevision } from "./revision"

export const BUSINESS_CONTEXT_RECOVERY_REQUESTED_EVENT = "teashop:business-context-recovery-requested"

export interface BusinessContextInvalidationDependencies {
  userStore: {
    clearScopedPermissionState: () => void
    disconnectBusinessWebSocket?: () => void
  }
  viewScopeStore: {
    stagePreferredOrganization: (scopeId: string) => void
  }
  invalidateSystemVersionRuntime: () => void
}

export interface BusinessContextInvalidationOptions {
  nextScopeId?: string
  requestRecovery?: boolean
}

export interface ExternalOrganizationPreferenceChange {
  key: string | null
  newValue: string | null
}

export function resolveExternalOrganizationScopeId(
  event: ExternalOrganizationPreferenceChange,
  accountId: string,
) {
  if (event.key === ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY) {
    if (!accountId || !event.newValue) return undefined
    try {
      const rememberedScopes = JSON.parse(event.newValue) as Record<string, unknown>
      const nextScopeId = rememberedScopes[accountId]
      return typeof nextScopeId === "string" && nextScopeId
        ? nextScopeId
        : undefined
    } catch {
      return undefined
    }
  }
  if (event.key === ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY && !accountId) {
    return event.newValue ?? ""
  }
  return undefined
}

/**
 * Invalidates only the runtime snapshot. Account-scoped organization and role
 * preferences are committed separately and must survive external auth changes.
 */
export function invalidateBusinessContextRuntime(
  deps: BusinessContextInvalidationDependencies,
  options: BusinessContextInvalidationOptions = {},
) {
  invalidateBusinessContextEpoch()
  advanceBusinessContextRevision()
  clearObservedScopeToken()
  deps.userStore.disconnectBusinessWebSocket?.()
  deps.userStore.clearScopedPermissionState()
  deps.invalidateSystemVersionRuntime()
  if (options.nextScopeId !== undefined) {
    deps.viewScopeStore.stagePreferredOrganization(options.nextScopeId)
  }
  if (options.requestRecovery && typeof window !== "undefined") {
    window.dispatchEvent(new Event(BUSINESS_CONTEXT_RECOVERY_REQUESTED_EVENT))
  }
}
